import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { Subscription } from 'rxjs';

import { Accessibility, CimaCore, Cloudinary, Formatter, Util, XmStore, XmStoreUtil } from 'core/services';
import { User } from 'store/user/models';
import { DataPlanType, UserRoles } from 'my-account/constants';

import { CmsCore, CmsDataDetail, CmsLineDataPlanCollection } from 'store/cms/models';
import { AccountSummary, LineSummary } from 'store/account-summary/models';
import { btgPlanNames, CBM_GEMINI_PLAN_CODES, CbmGeminiPlanType, CONFIG_TOKEN, DataPlanTypePlanEight, PLAN_EIGHT_PLAN_IDS, PlanEightPlanType, PromotionOffer, unlPlanNames } from 'core/constants';
import { planEligibilityDetails } from 'store/plan-eligibility/models';
import { targetPlanSwitchDetails } from 'store/plan-eligibility/models/target-plan-switch';
import { StoreAction } from 'store/actions';
import { DiscountPromoCollection, DiscountPromotion } from 'store/promotions/discount-promotion/models';
import { IXMOptions } from 'core/interfaces';

@Component({
    selector: 'xm-plan-data-options',
    styleUrls: ['./plan-data-options.scss'],
    templateUrl: './plan-data-options.html',
    standalone: false
})
export class XmPlanDataOptionsComponent implements OnInit, OnDestroy {
    @Input() public currentLine: LineSummary;
    @Input() public userRole: string;

    public inUse: string;
    public inUseIcon: MediaImageOptions;
    public miniIcon: MediaImageOptions;
    public dataIcon: MediaImageOptions;
    public byTheGigIcon: MediaImageOptions;
    public tabletIcon: MediaImageOptions;
    public errorText: string;
    public cmsDataDetailPageData: CmsDataDetail;
    public accountSummary: AccountSummary;
    public isBusinessUser: boolean;
    public user: User;
    public planDetails: planEligibilityDetails;
    public planEligibilityFail: boolean = false;
    public dataPlanPageData: CmsDataPlanDetails[];
    public isBTGScheduledSwitch: boolean = false;
    public btgPlanDetails: EligiblePlanDetails;
    public btgScheduledSwitchContent: EligiblePlanDetails;
    public lineSummary: LineSummary;
    public cmsSeg5TabletData: CmsSegmentFiveTablet;
    //promo
    public promoResponse: DiscountPromoCollection;
    public linePromotion: DiscountPromotion[];
    public accountPromotion: DiscountPromotion[];
    public tradeInDDAPromo: DiscountPromotion[];
    public extendedPromoDollar: DiscountPromotion[];
    public promotionIcon: MediaImageOptions;
    public shouldAccountShowPromotion: boolean = false;
    public servicePromotions: CmsServicePromotions;
    public promotionAccountHeader: string;
    public promotionAccountSubheader: string;
    public showPlanSixTabletSection: boolean;
    public isLoading: boolean = true;
    public isIcopPlanSwitch: boolean = false;
    public shouldDisableButton: boolean = false;
    public isCBMGeminiEnabled: boolean = false;
    public alertIcon: MediaImageOptions;
    public infoIconGrey: MediaImageOptions;
    public dollarIcon: MediaImageOptions;
    public caretIcon: MediaImageOptions;
    public promotionExpanded: boolean[] = [];
    
    private state: StateService;
    private xmStore: XmStore;
    private subscriptions: Subscription[] = [];
    private cimaCore: CimaCore;
    private config: IXMOptions;


    constructor(accessibility: Accessibility, xmStore: XmStore, state: StateService, cimaCore: CimaCore, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { accessibility, xmStore, state, cimaCore, config });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.isIcopPlanSwitch = this.config.ENABLE_ICOP_P1_PLANSWITCH;

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsDataDetail>(CmsDataDetail), (pageData: CmsDataDetail) => {
            this.cmsDataDetailPageData = pageData;
            this.cmsSeg5TabletData = pageData.segmentFiveTablet;
            this.miniIcon = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsDataDetailPageData.miniIcon)
            };
            this.inUseIcon = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsDataDetailPageData.inUseIcon)
            };
            this.dataIcon = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsDataDetailPageData.dataIcon)
            };
            this.byTheGigIcon = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsDataDetailPageData.byTheGigIcon)
            };
            this.tabletIcon = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsSeg5TabletData.icon)
            };
            this.alertIcon = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsDataDetailPageData.alertIcon)
            };

            this.infoIconGrey = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsDataDetailPageData.infoIconGrey)
            };

            this.dollarIcon = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsDataDetailPageData.dollarIcon)
            };

            this.inUse = this.cmsDataDetailPageData.inUse;
            this.errorText = this.cmsDataDetailPageData.errorText;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (cmsCore: CmsCore) => {
            this.caretIcon = {
                mobile: Cloudinary.generateMediaFromCms(cmsCore.icons.caretIcon)
            };
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (accountSummary: AccountSummary) => {
            this.accountSummary = accountSummary;
            this.isCBMGeminiEnabled = this.config.ENABLE_CBM_GEMINI && this.isBusinessUser && this.accountSummary.isCbmNoLevel;
            this.lineSummary = this.accountSummary.getLineByKey(this.state.params.line_id);
            this.showPlanSixTabletSection = this.accountSummary.level5Customer && this.lineSummary.isTablet;
            if (this.lineSummary.isUnlimited && !(this.accountSummary.level6Customer && !this.isBusinessUser) && !this.isCBMGeminiEnabled) {
                this.getPromotionContent(this.lineSummary);
            }
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
            this.dataPlanPageData = result.plans;
        }));

        let planEligibilityAction: string;

        if (this.config.ENABLE_CBM_GEMINI && this.isBusinessUser && this.accountSummary.isCbmNoLevel) {
            planEligibilityAction = StoreAction.GET_CBM_PLAN_ELIGIBILITY;
        } else if (!this.isBusinessUser && this.accountSummary.level6Customer) {
            planEligibilityAction = StoreAction.GET_PLAN_EIGHT_ELIGIBILITY;
        } else if (this.accountSummary.level5Customer || this.accountSummary.level6Customer) {
            planEligibilityAction = StoreAction.GET_PLAN_ELIGIBILITY;
        }
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.find<planEligibilityDetails>(planEligibilityAction, planEligibilityDetails, {
            params: { lineId: this.state.params.line_id },
            filters: { lineId: this.state.params.line_id }
        }), (result: planEligibilityDetails) => {
            this.planDetails = result;
            if (this.lineSummary.scheduledSwitch) {
                this.isBTGScheduledSwitch = this.BTGScheduledSwitch();
                if (this.isBTGScheduledSwitch && this.planDetails.eligiblePlans.BTG.length > 0) {
                    this.btgPlanDetails = this.planDetails.eligiblePlans.BTG[0];
                    this.btgScheduledSwitchContent = this.planDetails.eligiblePlans.BTG[1];
                }
            }
            this.isLoading = false;
        }, () => {
            this.isLoading = false;
        }));

        this.shouldDisableButton = this.userRole === UserRoles.MEMBER || this.userRole === UserRoles.VIEWER;
    }

    public generateFeatureIcon(image: CmsMediaImage): MediaImageOptions {
        return {
            mobile: Cloudinary.generateMediaFromCms(image)
        };
    }

    public getLineSwitchEligibility(planCode: string, planName: string, plan: EligiblePlanDetails): Promise<void> {
        this.planEligibilityFail = false; 

        let level6PlanName: string;
        let action: string;
        if (this.config.ENABLE_CBM_GEMINI && this.isBusinessUser && this.accountSummary.isCbmNoLevel) {
            action = StoreAction.GET_TARGET_PLAN_SWITCH_CBM;
            level6PlanName = (this.planDetails.currentPlan.planCode === CBM_GEMINI_PLAN_CODES.STANDARD) ? CbmGeminiPlanType.PRO : CbmGeminiPlanType.STANDARD;
        } else if (!this.isBusinessUser && this.accountSummary.level6Customer) {
            action = StoreAction.GET_TARGET_PLAN_8_SWITCH;
            level6PlanName = (this.planDetails.currentPlan.planName.toUpperCase() === DataPlanTypePlanEight.MOBILE_SELECT) || (this.planDetails.currentPlan.planCode === PLAN_EIGHT_PLAN_IDS.MOBILE_SELECT)?
                PlanEightPlanType.PLUS : PlanEightPlanType.SELECT;
        } else {
            action = unlPlanNames.includes(planName.toUpperCase()) ? StoreAction.GET_TARGET_PLAN_UNL_SWITCH : StoreAction.GET_TARGET_PLAN_BTG_SWITCH;
        }

        plan.promiseBtn = this.xmStore.fetchPromise<targetPlanSwitchDetails>(action, { params: {lineId: this.state.params.line_id, planName: level6PlanName }})
            .then((details: targetPlanSwitchDetails) => {
                if (unlPlanNames.includes(planName.toUpperCase())) {
                    this.state.go('account.activity.details.plan-switch.xm-unl-target-plan-review', { line_id: this.state.params.line_id, plan_code: planCode });
                } else if (btgPlanNames.includes(planName.toUpperCase()) && !this.isBTGScheduledSwitch){
                    this.state.go('account.activity.details.plan-switch.xm-btg-target-plan-review', { line_id: this.state.params.line_id, plan_code: planCode });
                } else if (btgPlanNames.includes(planName.toUpperCase()) && this.isBTGScheduledSwitch && details.scheduledTargetPlan){
                    this.state.go('account.activity.details.plan-switch.review', { line_id: this.state.params.line_id, plan_code: planCode });
                }
            }).catch(() => {
                this.planEligibilityFail = true;
            });  

        return plan.promiseBtn;
    }

    public BTGScheduledSwitch(): boolean {
        const planType: string = this.dataPlanPageData.find((data: CmsDataPlanDetails) => data.planCode.includes(this.lineSummary.scheduledSwitch.planCode)).planType;

        return planType === DataPlanType.BY_THE_GIG || planType === DataPlanType.BY_THE_GIG_SHARED;
    }


    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    // Displaying promo container temp. Needs to be replaced post CEUIDSS-29611 & CEUIDSS-29620 completion.
    public promotionLineHeader(linePromotion: DiscountPromotion): string {
        const promoHeader: string = linePromotion.promoBOID === PromotionOffer.BYOD_LL_SERP
            ? this.servicePromotions.promoBYOLineHeader
            : this.servicePromotions.promoLineHeader;

        return this.formatPromotionData(promoHeader, linePromotion.billerInfo?.monthlyPromotionValue);
    }

    public promotionLineSubheader(linePromotion: DiscountPromotion): string {
        return this.formatPromotionData(this.servicePromotions.BYOSubHeader, linePromotion.billerInfo?.monthlyPromotionValue, linePromotion.billerInfo?.promotionExpiration);
    }

    public promoAccountSubheader(accountPromotion: DiscountPromotion): string {
        return this.formatPromotionData(this.servicePromotions.subHeader, accountPromotion.billerInfo?.monthlyPromotionValue, accountPromotion.billerInfo?.promotionExpiration);
    }

    public promotionTradeInDDAHeader(tradeInPromotion: DiscountPromotion): string {
        return this.formatPromotionData(this.servicePromotions.promoTradeInDDAHeader, tradeInPromotion.billerInfo?.promotionAmount.toString());
    }

    public promotionExtendedDollarDDAHeader(extendedDollarPromo: DiscountPromotion): string {
        return this.formatPromotionData(this.servicePromotions.promoExtendedDollarDDAHeader, extendedDollarPromo.billerInfo?.promotionAmount.toString());
    }

    public isArray(value: string | string[]): boolean {
        return Array.isArray(value);
    }

    public togglePromotion(index: number): void {
        this.promotionExpanded[index] = !this.promotionExpanded[index];
    }
    
    public getPromoIcon(iconType: string): MediaImageOptions {
        switch (iconType?.toLowerCase()) {
            case 'warning':
                return this.alertIcon;
            case 'offer':
                return this.infoIconGrey;
            case 'savings':
                return this.dollarIcon;
            default:
                return this.alertIcon;
        }
    }

    private formatPromotionData(text: string, amount: string, date?: string): string {
        return text.replace('[AMOUNT]', amount).replace('[DATE]', Formatter.formatMonthDateYear(date));
    }

    private getPromotionContent(lineSummary: LineSummary): void {
        XmStoreUtil.defaultCatch(this.xmStore.findPromise<DiscountPromoCollection>(StoreAction.GET_DISCOUNT_PROMOTION, DiscountPromoCollection)
            .then((response: DiscountPromoCollection) => {
                this.promoResponse = response;
                this.servicePromotions = this.cmsDataDetailPageData.servicePromotions;
                this.promotionIcon = {
                    mobile: Cloudinary.generateMediaFromCms(this.servicePromotions.icon)
                };

                if (this.promoResponse.isAccountPromoEligible) {
                    this.accountPromotion = this.promoResponse.accountPromoEligible;
                    this.shouldAccountShowPromotion = this.promoResponse.isAccountPromoEligible;
                }

                if (this.promoResponse.isLinePromoUNLFREEEligible || this.promoResponse.isLinePromoBYOEligible) {
                    const lineUNLPromo: DiscountPromotion[] = this.promoResponse.linePromoUNLEligible.filter((promo: DiscountPromotion) => promo.lineId === lineSummary.id.key || promo.lineId === lineSummary.id.value);
                    const lineBYOPromo: DiscountPromotion[] = this.promoResponse.linePromoBYOEligible.filter((promo: DiscountPromotion) => promo.lineId === lineSummary.id.key || promo.lineId === lineSummary.id.value);

                    this.linePromotion = [...lineUNLPromo, ...lineBYOPromo];
                }

                if (this.promoResponse.tradeInDDAPromoEligible) {
                    this.tradeInDDAPromo = this.promoResponse.tradeInDDAPromoEligible.filter((promo: DiscountPromotion) => promo.lineId === lineSummary.id.key || promo.lineId === lineSummary.id.value);
                }

                if (this.promoResponse.extendedPromoDollarEligible) {
                    this.extendedPromoDollar = this.promoResponse.extendedPromoDollarEligible.filter((promo: DiscountPromotion) => promo.lineId === lineSummary.id.key || promo.lineId === lineSummary.id.value);
                }
            })
        );
    }
}
